import type { ComponentProps } from "solid-js";
import { cn } from "@stockpilot/shared";

/**
 * Theme-aware logistics/industrial line illustrations for empty states and
 * headers. They draw with `currentColor` + the primary accent token so they
 * adapt to every theme automatically. Decorative by default
 * (`aria-hidden`), since they always accompany a textual title/description.
 *
 * Style: light-weight outline (stroke 4 on a 160-box) with a single accent
 * fill — premium, restrained, not noisy.
 */
type IllustrationProps = ComponentProps<"svg">;

function baseClass(extra?: string) {
  return cn("text-primary/70", extra);
}

/** Empty shelves / racking — for "no products / no stock yet". */
export function EmptyShelvesIllustration(props: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden="true"
      class={baseClass(props.class)}
      {...props}
    >
      {/* Racking frame */}
      <g stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
        <path d="M28 22v116M132 22v116" />
        <path d="M22 138h116" opacity="0.5" />
        <path d="M28 58h104M28 96h104" />
      </g>
      {/* A couple of lonely boxes hinting "add stock" */}
      <g opacity="0.9">
        <rect
          x="44"
          y="34"
          width="32"
          height="24"
          rx="3"
          fill="hsl(var(--accent))"
          opacity="0.12"
          stroke="currentColor"
          stroke-width="4"
        />
        <path d="M44 44h32" stroke="currentColor" stroke-width="3" opacity="0.6" />
      </g>
      <g opacity="0.6">
        <rect
          x="92"
          y="72"
          width="28"
          height="22"
          rx="3"
          fill="hsl(var(--accent))"
          opacity="0.08"
          stroke="currentColor"
          stroke-width="4"
        />
      </g>
    </svg>
  );
}

/** Warehouse building — for "no warehouses yet". */
export function EmptyWarehouseIllustration(props: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden="true"
      class={baseClass(props.class)}
      {...props}
    >
      <g stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
        {/* Roofline */}
        <path d="M24 70 80 34l56 36" />
        {/* Walls */}
        <path d="M36 70v54h88V70" />
        {/* Roller door */}
        <rect x="64" y="86" width="32" height="38" rx="2" fill="hsl(var(--accent))" opacity="0.1" />
        <path d="M64 96h32M64 106h32M64 116h32" opacity="0.5" />
      </g>
      <path d="M22 124h116" stroke="currentColor" stroke-width="4" opacity="0.5" />
    </svg>
  );
}

/** Clipboard / order doc — for "no orders / operations / counts yet". */
export function EmptyOrdersIllustration(props: IllustrationProps) {
  return (
    <svg
      viewBox="0 0 160 160"
      fill="none"
      aria-hidden="true"
      class={baseClass(props.class)}
      {...props}
    >
      <g stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
        <rect
          x="40"
          y="32"
          width="80"
          height="96"
          rx="8"
          fill="hsl(var(--accent))"
          opacity="0.06"
        />
        {/* Clip */}
        <rect
          x="66"
          y="22"
          width="28"
          height="18"
          rx="5"
          fill="hsl(var(--accent))"
          opacity="0.14"
        />
        {/* Lines */}
        <path d="M56 64h48M56 84h48M56 104h28" opacity="0.6" />
      </g>
    </svg>
  );
}
